'use client'

import posthog from 'posthog-js'
import {useEffect, useState} from 'react'
import logger from '@/app/logger'
import {posthogApiKeyAPI} from "@/app/apiPaths";

let posthogInitialized = false

export function initPostHog(
  apiKey: string,
  host: string,
  userId?: string,
  userProperties?: Record<string, unknown>
) {
  if (!apiKey) {
    logger.warn('PostHog API key is empty, analytics will not be initialized')
    return
  }

  if (posthogInitialized) {
    logger.debug('PostHog already initialized')
    return
  }

  try {
    posthog.init(apiKey, {
      api_host: host,
      capture_pageview: true,
      autocapture: true,
      capture_pageleave: true,
      opt_out_capturing_by_default: false,
      persistence: 'sessionStorage',
    })

    posthogInitialized = true
    logger.debug('PostHog initialized')

    // Identify user if ID is provided
    if (userId) {
      identifyUser(userId, userProperties)
    }
  } catch (error) {
    logger.error({error}, 'Failed to initialize PostHog')
  }
}

/**
 * Identify a user in PostHog
 * @param userId User ID
 * @param properties User properties
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  if (!posthogInitialized) {
    logger.warn('PostHog not initialized, cannot identify user')
    return
  }

  try {
    posthog.identify(userId, properties)
    posthog.setPersonProperties(properties)
    logger.debug({userId, properties}, 'User identified in PostHog')
  } catch (error) {
    logger.error({error, userId, properties}, 'Failed to identify user in PostHog')
  }
}

/**
 * Track a custom event in PostHog
 * @param eventName Name of the event
 * @param properties Event properties
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (!posthogInitialized) {
    logger.warn(`PostHog not initialized, cannot track event: ${eventName}`)
    return
  }

  try {
    posthog.capture(eventName, properties)
    logger.debug({eventName, properties}, 'Event tracked in PostHog')
  } catch (error) {
    logger.error({error, eventName}, 'Failed to track event in PostHog')
  }
}

/**
 * Track a page view in PostHog
 * @param url Page URL
 * @param properties Additional properties
 */
export function trackPageView(url: string, properties?: Record<string, unknown>) {
  if (!posthogInitialized) {
    logger.warn(`PostHog not initialized, cannot track page view: ${url}`)
    return
  }

  try {
    posthog.capture('pageview', {$current_url: url, ...properties})
    logger.debug({url}, 'Page view tracked in PostHog')
  } catch (error) {
    logger.error({error, url}, 'Failed to track page view in PostHog')
  }
}

/**
 * Set user consent for analytics tracking
 * @param enabled Whether tracking is enabled
 */
export function setConsent(enabled: boolean) {
  if (!posthogInitialized) {
    logger.warn('PostHog not initialized, cannot set consent')
    return
  }

  try {
    if (enabled) {
      posthog.opt_in_capturing()
      posthog.consent.optInOut(true)
      logger.debug('User opted in to PostHog tracking')
    } else {
      posthog.opt_out_capturing()
      posthog.consent.optInOut(false)
      logger.debug('User opted out of PostHog tracking')
    }
  } catch (error) {
    logger.error({error, enabled}, 'Failed to set consent in PostHog')
  }
}

/**
 * Check if the user has given consent for analytics tracking
 * @returns Whether tracking is enabled
 */
export function hasConsent(): boolean {
  if (!posthogInitialized) {
    logger.warn('PostHog not initialized, cannot check consent')
    return false
  }

  try {
    return !posthog.has_opted_out_capturing()
  } catch (error) {
    logger.error({error}, 'Failed to check consent in PostHog')
    return false
  }
}

export function usePostHog(userId?: string, userProperties?: Record<string, unknown>) {
  const [initialized, setInitialized] = useState(posthogInitialized)

  useEffect(() => {
    if (posthogInitialized) {
      return
    }
    const fetchPostHogConfig = async () => {
      try {
        const response = await fetch(posthogApiKeyAPI())
        const {posthogApiKey, posthogHost} = await response.json()

        if (posthogApiKey) {
          initPostHog(posthogApiKey, posthogHost, userId, userProperties)
          setInitialized(true)
        } else {
          logger.warn('PostHog API key not found')
        }
      } catch (error) {
        logger.error({error}, 'Failed to fetch PostHog config')
      }
    }
    fetchPostHogConfig().then(() => logger.debug('PostHog config fetched'))
  }, [userId, userProperties])

  useEffect(() => {
    if (posthogInitialized && userId) {
      identifyUser(userId, userProperties)
    }
  }, [userId, userProperties]) // run again when props change

  return initialized
}
